import { useScrollReveal } from 'shared/hooks/useScrollReveal';
import { renderTitle } from 'shared/utils/renderTitle';
import { cn } from 'shared/utils/cn';
import { Eyebrow } from '../Eyebrow';
import { Heading } from '../Heading';

interface PageHeaderProps {
  label?: string;
  title: string;
  titleHighlight?: string;
  description?: string;
  italic?: boolean;
  children?: React.ReactNode;
  className?: string;
}

export function PageHeader({
  label,
  title,
  titleHighlight,
  description,
  italic,
  children,
  className,
}: PageHeaderProps) {
  const labelReveal = useScrollReveal({ animation: 'fade', delay: 0 });
  const headingReveal = useScrollReveal({ animation: 'float-up', delay: 120 });
  const descriptionReveal = useScrollReveal({ animation: 'float-up', delay: 240 });
  const childrenReveal = useScrollReveal({ animation: 'float-up', delay: 360 });

  return (
    <div className={className}>
      {label && (
        <Eyebrow
          innerRef={labelReveal.ref}
          style={labelReveal.style}
          className="tracking-[0.3em] mb-6 block"
        >
          {label}
        </Eyebrow>
      )}
      <Heading
        variant="display"
        innerRef={headingReveal.ref}
        style={headingReveal.style}
        className={cn('text-6xl md:text-8xl mb-8', italic && 'italic')}
      >
        {renderTitle(title, titleHighlight)}
      </Heading>
      {description && (
        <p
          ref={descriptionReveal.ref}
          style={descriptionReveal.style}
          className="font-sans text-xl text-on-surface-variant leading-relaxed max-w-2xl"
        >
          {description}
        </p>
      )}
      {children && (
        <div ref={childrenReveal.ref} style={childrenReveal.style}>
          {children}
        </div>
      )}
    </div>
  );
}
